import { Link } from 'react-router-dom';
import {
  AXES,
  LIBELLES_AXES,
  RUBRIQUES_TRAME,
  sommePoids,
  type ContactDetail,
  type EvaluationDetail,
} from '@rh/shared';
import { dateHeure } from '../format';
import { heureDeTunis, libelleContact, noteEnFrancais } from '../horsligne/textes';
import type { ResumeEnvoi } from '../horsligne/types';
import { Pastille } from './Pastille';

/** Évaluation figée : note, écart éventuel et sa justification, scores par axe. */
export function ResumeEvaluation({ evaluation }: { evaluation: EvaluationDetail }) {
  const total = sommePoids(evaluation.poids);
  const forcee = !evaluation.eliminatoire && evaluation.noteFinale !== evaluation.noteCalculee;
  return (
    <div className="evaluation">
      <div className="notes-calcul">
        <span className="note">
          <strong>{evaluation.noteFinale.toFixed(1)}</strong>
          <small>/5</small>
        </span>
        {evaluation.eliminatoire && (
          <span className="signal-alerte">Éliminatoire : {evaluation.motifEliminatoire}</span>
        )}
        {forcee && (
          <span className="signal-alerte">
            Note forcée — calculée {evaluation.noteCalculee.toFixed(1)}
          </span>
        )}
        <span className="mention">
          Barème v{evaluation.baremeVersion} · {evaluation.evalueePar} · {dateHeure(evaluation.evalueeLe)}
        </span>
      </div>
      {forcee && evaluation.justificationEcart && (
        <p className="justification">« {evaluation.justificationEcart} »</p>
      )}
      <table className="scores-table">
        <tbody>
          {AXES.filter((axe) => evaluation.scores[axe] !== undefined).map((axe) => (
            <tr key={axe}>
              <th scope="row">{LIBELLES_AXES[axe]}</th>
              <td>{evaluation.scores[axe]}/5</td>
              <td className="mention">
                {total ? Math.round((evaluation.poids[axe] / total) * 100) : 0} %
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ouiNon(valeur: boolean | null, oui: string, non: string): string | null {
  if (valeur === null) return null;
  return valeur ? oui : non;
}

/**
 * Ce que le téléphone garde encore pour un contact (docs/UI.md § Écran 3, mode
 * hors ligne) : jamais le contenu, seulement l'étape qui attend.
 */
function LigneSaisieLocale({ resume }: { resume: ResumeEnvoi }) {
  const lien = `/contacts/${resume.contactId}`;
  if (resume.etat === 'brouillon') {
    return (
      <p className="meta">
        Brouillon non enregistré sur ce téléphone · <Link to={lien}>reprendre</Link>
      </p>
    );
  }
  if (resume.etat === 'refuse') {
    return (
      <p className="meta">
        Envoi refusé : {resume.refus?.message ?? 'motif inconnu'} · <Link to={lien}>revoir</Link>
      </p>
    );
  }
  if (resume.etapes.fin === 'cloture') {
    return (
      <p className="meta">
        Clôture en attente d’envoi{resume.heureFin ? ` (saisie à ${heureDeTunis(resume.heureFin)})` : ''}
      </p>
    );
  }
  if (resume.etapes.fin === 'evaluation') {
    const details = [
      resume.noteAffichee !== null ? `note affichée ${noteEnFrancais(resume.noteAffichee)}` : null,
      resume.baremeVersion !== null ? `barème v${resume.baremeVersion}` : null,
    ].filter(Boolean);
    return (
      <p className="meta">
        Évaluation en attente d’envoi{details.length > 0 ? ` (${details.join(' · ')})` : ''}
      </p>
    );
  }
  return (
    <p className="meta">Modifications en attente d’envoi depuis ce téléphone ({heureDeTunis(resume.modifieLe)})</p>
  );
}

/** Saisies gardées sur le téléphone pour une fiche qui ne se lit pas hors ligne : sans nom ni contenu. */
export function SaisiesLocales({ resumes }: { resumes: readonly ResumeEnvoi[] }) {
  if (resumes.length === 0) return <p className="mention">Aucune saisie gardée sur ce téléphone pour ce candidat.</p>;
  return (
    <ul className="historique">
      {resumes.map((resume) => (
        <li key={resume.contactId} className="en-attente">
          <div className="ligne1">
            <strong>{libelleContact(resume.type, resume.debuteLe)}</strong>
          </div>
          <LigneSaisieLocale resume={resume} />
        </li>
      ))}
    </ul>
  );
}

/** Historique des contacts, du plus récent au plus ancien (docs/UI.md § Écran 2). */
export function HistoriqueContacts({
  contacts,
  utilisateurId,
  envois,
}: {
  contacts: ContactDetail[];
  utilisateurId: string | undefined;
  /** Saisies du compte connecté encore sur le téléphone, par contact. */
  envois?: ReadonlyMap<string, ResumeEnvoi>;
}) {
  if (contacts.length === 0) return <p className="mention">Aucun contact pour l’instant.</p>;
  return (
    <ul className="historique">
      {contacts.map((contact) => {
        const issues = [
          ouiNon(contact.joignable, 'Joint', 'Injoignable'),
          ouiNon(contact.disponibleDeSuite, 'Disponible de suite', 'Pas disponible de suite'),
          ouiNon(contact.interesse, 'Intéressé', 'Pas intéressé'),
        ].filter(Boolean);
        const rubriques = RUBRIQUES_TRAME.filter((r) => contact.reponses[r.cle]);
        const local = envois?.get(contact.id);
        return (
          <li key={contact.id} className={local ? 'en-attente' : undefined}>
            <div className="ligne1">
              {contact.type === 'telephonique' && <Pastille valeur={contact.potentiel} />}
              <strong>{contact.type === 'telephonique' ? 'Appel' : 'Entretien présentiel'}</strong>
              <span className="mention">
                {dateHeure(contact.debuteLe)} · {contact.auteur}
              </span>
              {!contact.termineLe && (
                <span className="signal-alerte">
                  en cours
                  {contact.auteurId === utilisateurId && (
                    <>
                      {' · '}
                      <Link to={`/contacts/${contact.id}`}>reprendre</Link>
                    </>
                  )}
                </span>
              )}
            </div>
            {issues.length > 0 && <p className="meta">{issues.join(' · ')}</p>}
            {(contact.rdvLe || contact.rappelLe) && (
              <p className="meta">
                {contact.rdvLe && (
                  <span>
                    Rendez-vous {dateHeure(contact.rdvLe)}
                    {contact.rdvLieu ? ` — ${contact.rdvLieu}` : ''}
                  </span>
                )}
                {contact.rappelLe && <span>Rappel {dateHeure(contact.rappelLe)}</span>}
              </p>
            )}
            {rubriques.length > 0 && (
              <dl className="reponses">
                {rubriques.map((rubrique) => (
                  <div key={rubrique.cle}>
                    <dt>{rubrique.libelle}</dt>
                    <dd>{contact.reponses[rubrique.cle]}</dd>
                  </div>
                ))}
              </dl>
            )}
            {contact.observations && <p className="observations">{contact.observations}</p>}
            {contact.evaluation && <ResumeEvaluation evaluation={contact.evaluation} />}
            {local && <LigneSaisieLocale resume={local} />}
          </li>
        );
      })}
    </ul>
  );
}
